import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, MessageSquare } from 'lucide-react';
import { insightArticles, faqs } from '../mock/data';

const Insights = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="display-large text-white mb-6">
              Intelligence Meets <span className="text-bluemp-400">Integrity</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8">
              Your platform for thought leadership and practical guidance on responsible AI implementation. Explore insights, best practices, and real-world perspectives from the front lines of AI transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">Latest Insights</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Stay ahead with practical insights on AI implementation, ethics, and business transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Featured Article */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20 dark-hover">
                <div className="flex items-center gap-2 text-bluemp-400 mb-4">
                  <Calendar size={16} />
                  <span className="body-small">{insightArticles[0].date}</span>
                  <span className="mx-2">•</span>
                  <Clock size={16} />
                  <span className="body-small">{insightArticles[0].readTime}</span>
                </div>

                <h3 className="heading-1 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {insightArticles[0].title}
                </h3>

                <p className="body-medium text-gray-300 mb-6">
                  {insightArticles[0].excerpt}
                </p>

                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 bg-bluemp-400/20 text-bluemp-400 rounded text-sm">
                    {insightArticles[0].category}
                  </span>
                  <a
                    href="/insights/ethics-of-ai.html"
                    className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors font-semibold"
                  >
                    Read Article <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insightArticles.slice(1).map((article) => {
              let fileName = '';
              switch (article.title) {
                case 'Practical AI Implementation: Lessons from South African Financial Services':
                  fileName = 'practical-ai-implementation.html';
                  break;
                case 'ROI Measurement in AI Projects: Beyond the Hype':
                  fileName = 'roi-measurement-ai.html';
                  break;
                case 'Data Quality: The Foundation of Successful AI Solutions':
                  fileName = 'data-quality-successful-ai.html';
                  break;
                default:
                  fileName = '#';
              }

              return (
                <div
                  key={article.id}
                  className="bg-gray-900/30 backdrop-blur-sm p-6 rounded-lg border border-bluemp-400/20 dark-hover"
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-3">
                    <Calendar size={14} />
                    <span className="body-small">{article.date}</span>
                    <span className="mx-2">•</span>
                    <Clock size={14} />
                    <span className="body-small">{article.readTime}</span>
                  </div>

                  <h3 className="heading-3 text-white mb-3 group-hover:text-bluemp-400 transition-colors">
                    {article.title}
                  </h3>

                  <p className="body-small text-gray-300 mb-4">{article.excerpt}</p>

                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-bluemp-400/20 text-bluemp-400 rounded text-xs">
                      {article.category}
                    </span>
                    <a
                      href={`/insights/${fileName}`}
                      className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors text-sm"
                    >
                      Read More <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Q&A Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">Frequently Asked Questions</h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Get answers to common questions about AI implementation, ethics, and working with Blue MP.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-900/30 backdrop-blur-sm p-6 rounded-lg border border-bluemp-400/20"
                >
                  <div className="flex items-start gap-4">
                    <MessageSquare size={24} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="heading-3 text-white mb-3">{faq.question}</h3>
                      <p className="body-medium text-gray-300">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">Ready to Apply These Insights?</h2>
          <p className="body-large text-gray-300 mb-8">
            Transform knowledge into action. Let's discuss how these insights can be applied to your specific AI challenges and opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book a Consultation
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions" className="btn-secondary">
              Explore Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
